import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  ViewContainerRef,
  output,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import { select, Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  Observable,
  combineLatest,
  filter,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AdminsSelectDialogComponent, AdminsSelectDialogResult } from '@core/account-smart-ui';
import type { User, UserBase } from '@core/account-types';
import { AdminsAvatarsComponent } from '@core/account-ui';
import type { AtcGroupedUsers } from '@core/candidate-data';
import {
  AccessToCandidatePolicy,
  filterOutUsersWithoutAccess,
  FullCandidateSelectors,
} from '@core/candidate-data';
import type { FullCandidate } from '@core/candidate-types';
import { filterDefined } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { CandidateInterviewTemplatesService } from '@core/interview-templates-logic';
import { OfferSelectComponent } from '@core/offers-smart-ui';
import { OfferKind } from '@core/offers-types';
import type { OfferSelectOption } from '@core/offers-ui';
import {
  BadgeComponent,
  SelectGroupedOptions,
  DialogService,
  DialogWidth,
  ButtonComponent,
  GroupComponent,
  DividerComponent,
  SelectComponent,
  LabelComponent,
  FormFieldComponent,
  HeadingComponent,
} from '@core/user-interface-legacy';

import type { AddEvaluationsDropdownModuleTranslations } from '../../translations';

@Component({
  selector: 'rt-request-evaluation-tab',
  templateUrl: 'request-evaluation-tab.component.html',
  styleUrls: ['./request-evaluation-tab.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeadingComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    SelectComponent,
    OfferSelectComponent,
    AdminsAvatarsComponent,
    DividerComponent,
    GroupComponent,
    ButtonComponent,
    TranslatePipe,
    BadgeComponent,
  ],
})
export class RequestEvaluationTabComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly i18n = inject(I18nService);
  private readonly dialogSvc = inject(DialogService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly store = inject(Store);
  private readonly candidateInterviewTemplatesService = inject(CandidateInterviewTemplatesService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly offers = input<OfferSelectOption[]>([]);
  public readonly hasOffers = input<boolean>(false);
  public readonly admins = input<UserBase[]>([]);
  public readonly canManageInterviewTemplates = input<boolean>(undefined!);
  public readonly candidate = input<FullCandidate>();
  public readonly candidateId = input<number>();
  public readonly allowedUsers = input<User[]>([]);
  public readonly offerId = input<number | null>();
  public readonly talentPoolId = input<number | null>();
  public readonly translations = input<AddEvaluationsDropdownModuleTranslations>(undefined!);
  public readonly allTemplates = input<SelectGroupedOptions[]>([]);
  @Input() public schedule: boolean = false;

  public submit = output<{
    talentPoolId?: number;
    offerId?: number;
    templateId: string;
    admins: User[];
  }>();
  public close = output<void>();

  public form: UntypedFormGroup;
  public canViewCandidates = false;

  protected visibleTemplates = signal<SelectGroupedOptions[]>([]);
  protected templatesLoaded = signal<boolean>(false);

  private ngOnDestroy$ = new Subject<void>();
  private disallowedUsers: User[] = [];
  private newAllowedUsers: User[] = [];

  constructor() {
    this.form = this.formBuilder.group({
      offerId: [''],
      templateId: ['', Validators.required],
      admins: [this.admins() || []],
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.hasOffers()) {
      const [firstOffer] = this.offers();
      const offerIdValue = this.offerId();
      const offerId =
        this.schedule || offerIdValue?.toString().length ? offerIdValue : firstOffer!.id;

      this.form.get('offerId')!.setValue(offerId);
    }

    this.checkAbility();
  }

  public ngOnInit(): void {
    this.selectUsersOnOfferChange()
      ?.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ usersWithAccess, usersWithoutAccess }: AtcGroupedUsers) => {
        if (!this.schedule) {
          this.form
            .get('admins')
            ?.setValue(
              filterOutUsersWithoutAccess(this.form.get('admins')?.value ?? [], usersWithAccess),
            );
          this.cdRef.detectChanges();
        }

        this.newAllowedUsers = usersWithAccess;
        this.disallowedUsers = usersWithoutAccess;
      });

    this.setDefaultAdmins();
    this.fetchTemplatesOnOfferChange();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public hasMoreThanOneOffer(): boolean {
    return this.offers().length > 1;
  }

  public requestInterview(): void {
    const { offerId, templateId, admins } = this.form.value;
    const isTalentPool =
      this.offers().find(offer => offerId === offer.id)?.kind === OfferKind.TalentPool;

    this.submit.emit({
      admins,
      templateId,
      ...(isTalentPool ? { talentPoolId: offerId } : { offerId }),
    });
  }

  public editAssignments(): void {
    const data = {
      assignments: this.form.get('admins')!.value.map((a: { id: number }) => a.id),
      heading: this.i18n.translate(this.translations().interview.scheduleDialog.assignmentsTitle),
      subheading: this.i18n.translate(this.translations().interview.scheduleDialog.assignmentsDesc),
      allowedUsers: this.newAllowedUsers,
      disallowedUsers: this.disallowedUsers,
    };
    const dialog = this.dialogSvc.openCustom(
      AdminsSelectDialogComponent,
      { width: DialogWidth.SmallX },
      data,
      this.viewContainerRef,
    );

    dialog.afterClosed.pipe(filterDefined).subscribe(({ users }: AdminsSelectDialogResult) => {
      if (users) {
        this.form.get('admins')!.setValue(users);
        this.cdRef.detectChanges();
      }
    });
  }

  private setDefaultAdmins(): void {
    this.form.get('admins')!.setValue(this.admins());
  }

  private checkAbility(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.canViewCandidates = enabledMap[Ability.ViewCandidates];
        this.cdRef.detectChanges();
      });
  }

  private fetchTemplatesOnOfferChange(): void {
    const candidateId = this.candidate()?.id || this.candidateId();

    this.form
      .get('offerId')!
      .valueChanges.pipe(
        startWith(this.form.get('offerId')!.value),
        distinctUntilChanged(),
        switchMap(offerId => {
          const isTalentPool =
            this.offers().find(offer => offerId === offer.id)?.kind === OfferKind.TalentPool;

          if (!isTalentPool && offerId && candidateId) {
            return combineLatest({
              result: this.candidateInterviewTemplatesService.fetchGroupedCandidateTemplates({
                candidateId,
                offerId,
                kind: 'interview',
              }),
              loaded: this.candidateInterviewTemplatesService.areCandidateTemplatesLoaded({
                candidateId,
                offerId,
                kind: 'interview',
              }),
            });
          }

          const fallbackGroups = this.allTemplates();
          return of({
            result: {
              groups: fallbackGroups,
              preselectedTemplateId: null,
            },
            loaded: true,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ result, loaded }) => {
        this.visibleTemplates.set(result.groups);
        this.templatesLoaded.set(loaded);
        const templateIdControl = this.form.get('templateId')!;
        if (!templateIdControl.value) {
          templateIdControl.setValue(
            result.preselectedTemplateId !== null ? result.preselectedTemplateId : '',
          );
        }
        this.cdRef.detectChanges();
      });
  }

  private selectUsersOnOfferChange(): Observable<AtcGroupedUsers> | undefined {
    return <Observable<AtcGroupedUsers> | undefined>this.form.get('offerId')?.valueChanges.pipe(
      filter(() => this.hasOffers()),
      startWith(
        !!this.offerId() && this.schedule
          ? this.offers().find(offer => offer.id === this.offerId())?.id
          : this.offers()[0]?.id,
      ),
      switchMap((offerId: number | string) => {
        const isTalentPool =
          this.offers().find(offer => offerId === offer.id)?.kind === OfferKind.TalentPool;

        return this.store.select(
          FullCandidateSelectors.groupedUsersInShareActionContext({
            candidateId: this.candidate()?.id!,
            ...(isTalentPool ? { talentPoolId: Number(offerId) } : { offerId: Number(offerId) }),
            atcPolicy: AccessToCandidatePolicy.Offer,
          }),
        );
      }),
    );
  }
}
